// Copyright (c) Ping. All rights reserved.
//
// Web host for the NEW Tasks RIGHT-PANEL (`<TasksPanel>`). Wires the
// shared presentational panel to the SAME live DeviceGroup-only task
// list the full-page `<TasksScreen>` host (`TasksRouteHost`) uses:
//
//   * `useTasks(webTasksPersister)` is the live source — a Dexie
//     `liveQuery` that re-renders on every local write AND on every
//     incoming `com.ping.task_upsert` / `task_delete` the foundations
//     apply (cross-device mirror, §2.10).
//   * Mutations are LOCAL-FIRST: write through `webTasksPersister`
//     (works fully offline / pre-SDK), then best-effort mirror to the
//     DeviceGroup via `sendTaskUpsert` / `sendTaskDelete`.
//
// The wire `TaskRecord` is flat (id/title/due/done/list/position). The
// panel renders a richer model (folders, priority, assignee,
// description); the lossless wire fields round-trip (title/done/due and
// `list`→folder), while priority/assignee/description default the same
// way the existing `taskRecordToItem` adapter defaults them. The panel
// keeps any in-session edits to those non-persisted fields locally.

import { useCallback, useMemo } from 'react';

import {
  type TaskFolder,
  type TaskFolderId,
  type TaskId,
  type TaskItem,
  type TaskRecord,
  taskRecordToItem,
  type UserId,
  useSession,
  useTasks,
} from '@ping/ui';
// `TasksPanel` + its prop types are exported from the tasks barrel only
// (the root `@ping/ui` index is owned by another worker). Import them
// via the package subpath — same pattern `deviceGroupTaskSync` uses for
// `@ping/ui/src/conversation/mlsBytes`.
import {
  type TaskDraft,
  type TaskMember,
  TasksPanel,
} from '@ping/ui/src/tasks';

import { webTasksPersister } from '@core/persistence/db';
import { sendTaskUpsert } from '@features/tasks/deviceGroupTaskSync';

// A task with no `list` lives in this synthetic default folder so the
// panel always has at least one list to open into.
const DEFAULT_LIST = 'Tasks';

/** Lex-sortable position; appended at the end of the list. */
function nextPosition(): string {
  // Zero-padded ms keeps the lexical order monotonic for new rows.
  return Date.now().toString().padStart(16, '0');
}

/** Stable-ish task id for newly created tasks. */
function newTaskId(): string {
  return `task_${Date.now().toString(16)}${Math.random().toString(16).slice(2, 8)}`;
}

export function TasksPanelHost(): JSX.Element {
  const { client, identity } = useSession();
  const rows = useTasks(webTasksPersister);

  // ----- Derive the panel's rich model from the flat wire rows -----

  // Distinct `list` values become folders ("lists"). Every task gets a
  // folder: rows with no `list` fall into the DEFAULT_LIST bucket.
  const folders = useMemo<readonly TaskFolder[]>(() => {
    const counts = new Map<string, number>();
    for (const r of rows) {
      const list = r.list && r.list.trim() !== '' ? r.list : DEFAULT_LIST;
      counts.set(list, (counts.get(list) ?? 0) + 1);
    }
    // Always surface the default list so "New task" has a home even when
    // empty — but only when there is at least one task OR no folders at
    // all yet (so the EMPTY state can show on a truly empty list).
    return Array.from(counts.entries()).map(([title, taskCount]) => ({
      id: title as unknown as TaskFolderId,
      title,
      taskCount,
    }));
  }, [rows]);

  const tasks = useMemo<readonly TaskItem[]>(
    () =>
      rows.map((r) => {
        const item = taskRecordToItem(r);
        const list = r.list && r.list.trim() !== '' ? r.list : DEFAULT_LIST;
        return { ...item, folderId: list as unknown as TaskFolderId };
      }),
    [rows],
  );

  // Persisted descriptions, keyed by task id, surfaced to the panel's
  // read + edit views so the body round-trips through reload.
  const descriptionsById = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const r of rows) {
      if (r.description !== undefined && r.description !== '') {
        out[r.id] = r.description;
      }
    }
    return out;
  }, [rows]);

  // The panel only needs a self member for the "You" assignee pill; the
  // wire model has no roster. We surface the current user as self.
  const members = useMemo<readonly TaskMember[]>(() => {
    const bytes = identity?.identityExport;
    const id = (
      bytes && bytes.length > 0
        ? Array.from(bytes.slice(0, 8))
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('')
        : 'self'
    ) as unknown as UserId;
    return [{ id, name: 'You', isSelf: true }];
  }, [identity]);

  // ----- Mutations (local-first, then best-effort DeviceGroup mirror) -----

  const byId = useMemo(() => {
    const m = new Map<string, TaskRecord>();
    for (const r of rows) m.set(r.id, r);
    return m;
  }, [rows]);

  const upsertRecord = useCallback(
    async (record: TaskRecord) => {
      await webTasksPersister.put(record);
      void sendTaskUpsert(client, record);
    },
    [client],
  );

  const onToggleComplete = useCallback(
    (task: TaskItem) => {
      const current = byId.get(task.id);
      if (!current) return;
      const next: TaskRecord = {
        ...current,
        done: task.state !== 'completed',
        updatedAt: Date.now(),
      };
      void upsertRecord(next);
    },
    [byId, upsertRecord],
  );

  const onUpsertTask = useCallback(
    (input: { id: TaskId | null; folderId: TaskFolderId | null; draft: TaskDraft }) => {
      const { id, folderId, draft } = input;
      const list = folderId ? (folderId as unknown as string) : DEFAULT_LIST;
      const existing = id ? byId.get(id) : undefined;
      const next: TaskRecord = {
        ...(existing ?? {}),
        id: existing?.id ?? newTaskId(),
        title: draft.title,
        position: existing?.position ?? nextPosition(),
        updatedAt: Date.now(),
        done: draft.state === 'completed',
        list,
        // Persist the richer form fields so they survive reload + mirror
        // cross-device. All additive / undefined-safe.
        state: draft.state,
        priority: draft.priority,
        description: draft.description,
      };
      // Due date + assignee are clearable: set when present, otherwise
      // strip any value carried over from `existing` (delete keeps the
      // record `exactOptionalPropertyTypes`-clean — no `undefined` slot).
      if (draft.dueAt !== null) next.due = draft.dueAt;
      else delete next.due;
      if (draft.assigneeId !== null) next.assigneeId = draft.assigneeId as unknown as string;
      else delete next.assigneeId;
      void upsertRecord(next);
    },
    [byId, upsertRecord],
  );

  // Creating a folder with no tasks yet: seed an empty placeholder task is
  // undesirable, so a "new folder/list" simply creates a task bucket the
  // user fills via "New task". We surface it by creating a single hidden
  // anchor only when the user adds a task; here we no-op the rename-only
  // case and rely on the first task carrying the `list`.
  const onCreateFolder = useCallback(
    (title: string) => {
      // Materialize the folder by creating an initial task so the list
      // appears in the tree; the panel then navigates the user in to add
      // more. Keeps the wire model (which has no standalone folder row)
      // consistent.
      const next: TaskRecord = {
        id: newTaskId(),
        title: 'New task',
        position: nextPosition(),
        updatedAt: Date.now(),
        done: false,
        list: title,
      };
      void upsertRecord(next);
    },
    [upsertRecord],
  );

  return (
    <TasksPanel
      folders={folders}
      tasks={tasks}
      members={members}
      descriptionsById={descriptionsById}
      onToggleComplete={onToggleComplete}
      onUpsertTask={onUpsertTask}
      onCreateFolder={onCreateFolder}
    />
  );
}
